import type { AuditLogEntry } from '@/mocks/auditLogs';

function pickString(raw: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuidLike(s: string): boolean {
  return UUID_RE.test(s.trim());
}

function normalizeUserRole(raw: string | undefined): AuditLogEntry['userRole'] {
  const s = (raw ?? '').toLowerCase().trim();
  if (s === 'admin') return 'admin';
  if (s === 'analyst' || s === 'editor' || s === 'researcher' || s === 'leader') return 'analyst';
  if (s === 'operator') return 'operator';
  if (s === 'viewer' || s === 'user') return 'viewer';
  return 'viewer';
}

function normalizeResult(raw: string | undefined): AuditLogEntry['result'] {
  const s = (raw ?? '').toLowerCase();
  if (s === 'failure' || s === 'error' || s === 'failed' || s === 'fail') return 'failure';
  if (s === 'warning' || s === 'warn') return 'warning';
  return 'success';
}

function normalizeResultFromDto(raw: Record<string, unknown>): AuditLogEntry['result'] {
  const sc = raw.statusCode ?? raw.status_code;
  if (typeof sc === 'number' && Number.isFinite(sc)) {
    if (sc >= 400) return sc >= 500 ? 'failure' : 'warning';
    return 'success';
  }
  return normalizeResult(pickString(raw, ['result', 'status', 'outcome']));
}

function humanFromLabel(label: string): string | undefined {
  const t = label.trim();
  if (!t) return undefined;
  const sep = ' — ';
  const idx = t.indexOf(sep);
  if (idx !== -1) {
    const right = t.slice(idx + sep.length).trim();
    if (right) return right;
  }
  return t;
}

function stripIdsDeep(val: unknown): unknown {
  if (val === null || val === undefined) return val;
  if (typeof val === 'string') {
    return isUuidLike(val) ? undefined : val;
  }
  if (typeof val !== 'object') return val;
  if (Array.isArray(val)) {
    const arr = val.map(stripIdsDeep).filter((x) => x !== undefined);
    return arr.length ? arr : undefined;
  }
  const o = val as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (k === 'id' || k === 'rule_ids' || k.endsWith('_id')) continue;
    const sv = stripIdsDeep(v);
    if (sv === undefined) continue;
    if (typeof sv === 'object' && sv !== null && !Array.isArray(sv) && Object.keys(sv).length === 0)
      continue;
    out[k] = sv;
  }
  return Object.keys(out).length ? out : undefined;
}

function metadataToDetails(meta: unknown): string {
  if (meta === null || meta === undefined) return '';
  if (typeof meta !== 'object') return '';
  const stripped = stripIdsDeep(meta);
  if (stripped === undefined || stripped === null) return '';
  if (
    typeof stripped === 'object' &&
    stripped !== null &&
    !Array.isArray(stripped) &&
    Object.keys(stripped).length === 0
  )
    return '';
  try {
    return JSON.stringify(stripped);
  } catch {
    return '';
  }
}

function parseNestedResource(raw: Record<string, unknown>): {
  resource: string;
  resourceSubtitle?: string;
  resourceRef: string;
} {
  const r = raw.resource;
  if (!r || typeof r !== 'object' || Array.isArray(r)) {
    const flat = pickString(raw, ['resource', 'resource_type', 'target']) ?? '—';
    const rid = pickString(raw, ['resource_id', 'resourceId', 'target_id']);
    const resourceRef = rid && !isUuidLike(rid) ? rid : '—';
    return { resource: flat, resourceRef };
  }
  const o = r as Record<string, unknown>;
  const kind = typeof o.kind === 'string' ? o.kind.trim() : '';
  const name = typeof o.name === 'string' && o.name.trim() ? o.name.trim() : '';
  const labelRaw = typeof o.label === 'string' ? o.label.trim() : '';
  const labelHuman = labelRaw ? humanFromLabel(labelRaw) : undefined;
  const codeRaw = typeof o.code === 'string' ? o.code.trim() : '';

  const primary = name || labelHuman || kind || '—';
  const resourceSubtitle = kind && primary !== kind ? kind : undefined;

  let resourceRef = '—';
  if (codeRaw && !isUuidLike(codeRaw)) resourceRef = codeRaw;
  else if (name) resourceRef = name;

  return {
    resource: primary,
    resourceSubtitle,
    resourceRef,
  };
}

function parseNestedUser(raw: Record<string, unknown>): {
  display: string;
  subtitle?: string;
  role: AuditLogEntry['userRole'];
} {
  const nested = raw.user;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const o = nested as Record<string, unknown>;
    const display =
      pickString(o, ['display_name', 'full_name']) ?? pickString(o, ['username']) ?? '—';
    const username = pickString(o, ['username']);
    const subtitle = username && display !== username ? `@${username}` : undefined;
    const role = normalizeUserRole(pickString(o, ['role', 'role_slug']));
    return { display, subtitle, role };
  }
  const flatUser = pickString(raw, ['user', 'username', 'user_name', 'actor']);
  return {
    display: typeof flatUser === 'string' ? flatUser : '—',
    subtitle: undefined,
    role: normalizeUserRole(pickString(raw, ['user_role', 'userRole', 'role', 'role_slug'])),
  };
}

/**
 * Chuẩn hoá một dòng audit từ BE (tên field có thể khác nhau) → {@link AuditLogEntry}.
 * Không đưa UUID vào các trường hiển thị; `id` chỉ dùng làm key nội bộ / rowKey.
 */
export function mapAuditLogDtoToEntry(raw: Record<string, unknown>, index: number): AuditLogEntry {
  const id = pickString(raw, ['id', 'log_id', 'uuid']) ?? `audit-${index}`;
  const ts =
    pickString(raw, ['create_date', 'timestamp', 'created_at', 'occurred_at', 'time']) ??
    new Date().toISOString();

  const { display: user, subtitle: userSubtitle, role: userRole } = parseNestedUser(raw);
  const action = pickString(raw, ['action', 'event', 'operation']) ?? '—';
  const { resource, resourceSubtitle, resourceRef } = parseNestedResource(raw);

  const meta = raw.metadata;
  const details =
    meta !== undefined && meta !== null && typeof meta === 'object'
      ? metadataToDetails(meta)
      : (pickString(raw, ['details', 'message', 'description']) ?? '');

  const result = normalizeResultFromDto(raw);
  const ipAddress = pickString(raw, ['ip_address', 'ip', 'ipAddress']) ?? '—';

  /** Redux/cache RTKQ: không giữ field optional là `undefined` (middleware serialize có thể coi là không serializable). */
  const entry: AuditLogEntry = {
    id,
    timestamp: ts,
    user,
    userRole,
    action,
    resource,
    resourceId: resourceRef,
    result,
    ipAddress,
    details,
  };
  if (userSubtitle !== undefined) entry.userSubtitle = userSubtitle;
  if (resourceSubtitle !== undefined) entry.resourceSubtitle = resourceSubtitle;
  return entry;
}
